import { RunContextApp } from "framework/RCApp";
import { UserRoleInfo } from "types/apis/sessionInterfaces";

export enum USER_ROLE {
  TPM           = 'TPM',
  CSM           = 'CSM',
  PM            = 'PM',
  UTILITY       = 'UTILITY',
  SUPER_ADMIN   = 'SUPER_ADMIN',
  PE            = 'PE',
  DELIVERY      = 'DELIVERY',
  FULL_ACCESS   = 'FULL_ACCESS'
}

export class PermissionsManager {
  private userRoleInfo!: UserRoleInfo;

  constructor(private rc: RunContextApp) {}

  setUserRoleInfo(userRoleInfo: UserRoleInfo) {
    this.userRoleInfo = userRoleInfo;
  }

  getUserRole(): USER_ROLE {
    return this.userRoleInfo.role.name as USER_ROLE;
  }

  getUserRoleDescription(): string {
    return this.userRoleInfo?.role?.description;
  }

  // Helper method to safely check permissions
  private hasPermission(permission: string): boolean {
    return this.userRoleInfo?.permissions?.includes(permission) || false;
  }

  canFeatureWorkflowFind(): boolean {
    return this.userRoleInfo.permissions.includes('api::feature-workflow.feature-workflow.find');
  }

  canSystemNodeFind(): boolean {
    return this.userRoleInfo.permissions.includes('api::system-node.system-node.find');
  }

  canSystemNodePositionFind(): boolean {
    return this.userRoleInfo.permissions.includes('api::system-node-position.system-node-position.find');
  }

  canUtilityCreate(): boolean {
    return this.userRoleInfo.permissions.includes('api::utility.utility-internal-apis.createUtility');
  }

  canUtilityApplianceProfileUpdate(): boolean {
    return this.userRoleInfo.permissions.includes('api::utility-appliance-profile.utility-appliance-profile-internal-apis.updateApplianceProfile');
  }

  canUtilityHomeProfileUpdate(): boolean {
    return this.userRoleInfo.permissions.includes('api::utility-home-profile.utility-home-profile-internal-apis.updateHomeProfile');
  }

  canRecommendationCreate(): boolean {
    return this.userRoleInfo.permissions.includes('api::recommendation.reco-internal-apis.createReco');
  }

  canRecommendationPublishAll(): boolean {
    return this.userRoleInfo.permissions.includes('api::recommendation.reco-internal-apis.publishAll');
  }

  canRecoScoreUpdate(): boolean {
    return this.userRoleInfo.permissions.includes('api::utility-reco-score.utility-reco-score-internal-apis.updateRecoScore');
  }

  canViewRecommendationExclusions(): boolean {
    return this.getUserRole() !== USER_ROLE.UTILITY;
  }

  // ==========================================================================
  // CONFIG REGISTRY PERMISSIONS
  // ==========================================================================

  /**
   * Check if user can view config registry (all roles can view)
   */
  canConfigRegistryView(): boolean {
    const role = this.getUserRole();
    return [
      USER_ROLE.UTILITY,
      USER_ROLE.CSM,
      USER_ROLE.TPM,
      USER_ROLE.PM,
      USER_ROLE.DELIVERY,
      USER_ROLE.PE,
      USER_ROLE.FULL_ACCESS
    ].includes(role);
  }

  /**
   * Check if user can view all configs including READY_FOR_QA and REJECTED
   * (only editors and above)
   */
  canConfigRegistryViewAll(): boolean {
    const role = this.getUserRole();
    return [
      USER_ROLE.PE,
      USER_ROLE.DELIVERY,
      USER_ROLE.FULL_ACCESS
    ].includes(role);
  }

  /**
   * Check if user can create/edit configs
   */
  canConfigRegistryEdit(): boolean {
    const role = this.getUserRole();
    return [
      USER_ROLE.DELIVERY,
      USER_ROLE.PE,
      USER_ROLE.FULL_ACCESS
    ].includes(role);
  }

  /**
   * Check if user can approve/reject configs
   */
  canConfigRegistryApprove(): boolean {
    const role = this.getUserRole();
    return [
      USER_ROLE.PM,
      USER_ROLE.FULL_ACCESS
    ].includes(role);
  }

  /**
   * Check if user is config admin (bulk ops, bypass approval)
   */
  isConfigRegistryAdmin(): boolean {
    return this.getUserRole() === USER_ROLE.FULL_ACCESS;
  }

  // ==========================================================================
  // CONFIG REGISTRY TEMPLATE PERMISSIONS
  // ==========================================================================

  /**
   * Check if user can view templates
   * All CONFIG_VIEWERS can view templates
   */
  canTemplateView(): boolean {
    const role = this.getUserRole();
    return this.canConfigRegistryView();
  }

  /**
   * Check if user can edit templates (create/edit/delete templates, add/remove configs, update values)
   * Only PM and FULL_ACCESS roles can edit templates
   */
  canTemplateEdit(): boolean {
    const role = this.getUserRole();
    return [
      USER_ROLE.PM,
      USER_ROLE.FULL_ACCESS
    ].includes(role);
  }

}
